import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { DateRangePicker, defaultStaticRanges, createStaticRanges } from "react-date-range";
import { useSelector } from 'react-redux';
import { enUS } from "date-fns/locale";
import moment from "moment-timezone";

const Calendar = ({ onCancel, onChange, initialRange }) => {
  const [state, setState] = useState([
    {
      startDate: initialRange.startDate || null,
      endDate: initialRange.endDate || null,
      key: "selection",
    },
  ]);

  const [isDatesSelected, setIsDatesSelected] = useState(false);
  const [dateState, setDateState] = useState(null);
  const [dateRangeState, setDateRangeState] = useState(null);
  const [months, setMonths] = useState(2);
  const { appSettings } = useSelector((state) => state.appSettings);
  const isUserSelectingRef = useRef(false); // Track if user is actively selecting dates

  useEffect(() => {
    // Don't reset state if user is actively selecting dates
    if (isUserSelectingRef.current) {
      return;
    }
    
    // Only update state if the initialRange dates are actually different from current state
    // This prevents resetting the user's selection when the parent re-renders
    const currentStart = state[0].startDate;
    const currentEnd = state[0].endDate;
    const newStart = initialRange.startDate || null;
    const newEnd = initialRange.endDate || null;
    
    // Compare dates by their time values
    const datesChanged = 
      (!currentStart && newStart) ||
      (currentStart && !newStart) ||
      (currentStart && newStart && currentStart.getTime() !== newStart.getTime()) ||
      (!currentEnd && newEnd) ||
      (currentEnd && !newEnd) ||
      (currentEnd && newEnd && currentEnd.getTime() !== newEnd.getTime());
    
    if (datesChanged) {
    setState([
      {
          startDate: newStart,
          endDate: newEnd,
        key: "selection",
      },
    ]);
    }
  }, [initialRange]);

  useEffect(() => {
    setIsDatesSelected(state[0].startDate !== null && state[0].endDate !== null);
  }, [state]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1200px)');

    const handleMediaQueryChange = (e) => {
      if (e.matches) {
        setMonths(1);
      } else {
        setMonths(2);
      }
    };

    // Set the initial value
    handleMediaQueryChange(mediaQuery);

    // Add the event listener
    mediaQuery.addEventListener('change', handleMediaQueryChange);

    // Cleanup event listener on component unmount
    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  // Helper to normalize dates consistently (matches handleOnChange logic)
  const normalizeDateForComparison = (date) => {
    if (!date) return null;
    // Apply same normalization as handleOnChange: timezone conversion then start of day
    const tzDate = new Date(moment.tz(date, global.config.getTimeZone()).toDate());
    return new Date(tzDate.getFullYear(), tzDate.getMonth(), tzDate.getDate());
  };

  // Helper to get today in the configured timezone
  const getTodayInTimezone = () => {
    return new Date(moment.tz(global.config.getTimeZone()).toDate());
  };

  const staticRanges = [
    ...defaultStaticRanges.slice(0, 2),
    ...createStaticRanges([
      {
        range: () => {
          const today = getTodayInTimezone();
          const weekStartDay = appSettings?.weekStartDay === "Sunday" ? 0 : 1;

          const startDate = new Date(today);
          const endDate = new Date(today);

          // Calculate offset to get to the start of the current week
          const offset = (today.getDay() + 7 - weekStartDay) % 7;
          startDate.setDate(today.getDate() - offset);
          endDate.setDate(startDate.getDate() + 6);

          return {
            startDate,
            endDate,
            key: "selection",
          };
        },
        isSelected: (range) => {
          if (!range || !range.startDate || !range.endDate) return false;
          
          const today = getTodayInTimezone();
          const weekStartDay = appSettings?.weekStartDay === "Sunday" ? 0 : 1;

          const startDate = new Date(today);
          const endDate = new Date(today);

          // Calculate offset to get to the start of the current week
          const offset = (today.getDay() + 7 - weekStartDay) % 7;
          startDate.setDate(today.getDate() - offset);
          endDate.setDate(startDate.getDate() + 6);

          // Normalize both ranges using the same logic as handleOnChange
          const normalizedRangeStart = normalizeDateForComparison(range.startDate);
          const normalizedRangeEnd = normalizeDateForComparison(range.endDate);
          const normalizedExpectedStart = normalizeDateForComparison(startDate);
          const normalizedExpectedEnd = normalizeDateForComparison(endDate);

          return (
            normalizedRangeStart?.getTime() === normalizedExpectedStart?.getTime() &&
            normalizedRangeEnd?.getTime() === normalizedExpectedEnd?.getTime()
          );
        },
        label: "This Week",
      },
      {
        range: () => {
          const today = getTodayInTimezone();
          const weekStartDay = appSettings?.weekStartDay === "Sunday" ? 0 : 1;

          // Calculate the end date of last week (day before the start of this week)
          const thisWeekStart = new Date(today);
          const offset = (today.getDay() + 7 - weekStartDay) % 7;
          thisWeekStart.setDate(today.getDate() - offset);
          
          // Last week ends the day before this week starts
          const endDate = new Date(thisWeekStart);
          endDate.setDate(endDate.getDate() - 1);
          
          // Last week starts 6 days before the end date
          const startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 6);

          return {
            startDate,
            endDate,
            key: "selection",
          };
        },
        isSelected: (range) => {
          if (!range || !range.startDate || !range.endDate) return false;
          
          const today = getTodayInTimezone();
          const weekStartDay = appSettings?.weekStartDay === "Sunday" ? 0 : 1;

          // Calculate the end date of last week (day before the start of this week)
          const thisWeekStart = new Date(today);
          const offset = (today.getDay() + 7 - weekStartDay) % 7;
          thisWeekStart.setDate(today.getDate() - offset);
          
          // Last week ends the day before this week starts
          const endDate = new Date(thisWeekStart);
          endDate.setDate(endDate.getDate() - 1);
          
          // Last week starts 6 days before the end date
          const startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 6);

          // Normalize both ranges using the same logic as handleOnChange
          const normalizedRangeStart = normalizeDateForComparison(range.startDate);
          const normalizedRangeEnd = normalizeDateForComparison(range.endDate);
          const normalizedExpectedStart = normalizeDateForComparison(startDate);
          const normalizedExpectedEnd = normalizeDateForComparison(endDate);

          return (
            normalizedRangeStart?.getTime() === normalizedExpectedStart?.getTime() &&
            normalizedRangeEnd?.getTime() === normalizedExpectedEnd?.getTime()
          );
        },
        label: "Last Week",
      },
      {
        range: () => {
          const today = getTodayInTimezone();
          const weekStartDay = appSettings?.weekStartDay === 'Sunday' ? 1 : 0;

          // Calculate the end date (Sunday of this week if week starts on Monday, Saturday of last week otherwise)
          const endDate = new Date(today);
          if (today.getDay() !== weekStartDay) {
            endDate.setDate(
              today.getDate() - ((today.getDay() + 7 + weekStartDay) % 7)
            );
          } else if (weekStartDay === 0) {
            // If week starts on Sunday
            endDate.setDate(today.getDate() - 1); // Set end date to yesterday
          }

          // Calculate the start date (Monday of the week before last if week starts on Monday, Sunday of the week before last otherwise)
          const startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 13); // Start date is 14 days before the end date

          return {
            startDate,
            endDate,
            key: "selection",
          };
        },
        isSelected: (range) => {
          if (!range || !range.startDate || !range.endDate) return false;
          
          const today = getTodayInTimezone();
          const weekStartDay = appSettings?.weekStartDay === 'Sunday' ? 1 : 0;

          // Calculate the end date (Sunday of this week if week starts on Monday, Saturday of last week otherwise)
          const endDate = new Date(today);
          if (today.getDay() !== weekStartDay) {
            endDate.setDate(
              today.getDate() - ((today.getDay() + 7 + weekStartDay) % 7)
            );
          } else if (weekStartDay === 0) {
            // If week starts on Sunday
            endDate.setDate(today.getDate() - 1); // Set end date to yesterday
          }

          // Calculate the start date (Monday of the week before last if week starts on Monday, Sunday of the week before last otherwise)
          const startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 13); // Start date is 14 days before the end date

          // Normalize both ranges using the same logic as handleOnChange
          const normalizedRangeStart = normalizeDateForComparison(range.startDate);
          const normalizedRangeEnd = normalizeDateForComparison(range.endDate);
          const normalizedExpectedStart = normalizeDateForComparison(startDate);
          const normalizedExpectedEnd = normalizeDateForComparison(endDate);

          return (
            normalizedRangeStart?.getTime() === normalizedExpectedStart?.getTime() &&
            normalizedRangeEnd?.getTime() === normalizedExpectedEnd?.getTime()
          );
        },
        label: 'Past Two Weeks',
      },
    ]),
    ...defaultStaticRanges.slice(4),
    ...createStaticRanges([
      {
        range: (value) => ({
          startDate: new Date(new Date().getFullYear(), 0, 1), // This year
          endDate: new Date(new Date().getFullYear(), 11, 31), // End of this year
          key: "selection",
        }),
        isSelected: (range) => {
          if (!range || !range.startDate || !range.endDate) return false;
          const normalizedRangeStart = normalizeDateForComparison(range.startDate);
          const normalizedRangeEnd = normalizeDateForComparison(range.endDate);
          const expectedStart = normalizeDateForComparison(new Date(new Date().getFullYear(), 0, 1));
          const expectedEnd = normalizeDateForComparison(new Date(new Date().getFullYear(), 11, 31));
          return (
            normalizedRangeStart?.getTime() === expectedStart?.getTime() &&
            normalizedRangeEnd?.getTime() === expectedEnd?.getTime()
          );
        },
        label: 'This Year',
      },
      {
        range: (value) => ({
          startDate: new Date(new Date().getFullYear() - 1, 0, 1), // Last year
          endDate: new Date(new Date().getFullYear() - 1, 11, 31),
          key: "selection",
        }),
        isSelected: (range) => {
          if (!range || !range.startDate || !range.endDate) return false;
          const normalizedRangeStart = normalizeDateForComparison(range.startDate);
          const normalizedRangeEnd = normalizeDateForComparison(range.endDate);
          const expectedStart = normalizeDateForComparison(new Date(new Date().getFullYear() - 1, 0, 1));
          const expectedEnd = normalizeDateForComparison(new Date(new Date().getFullYear() - 1, 11, 31));
          return (
            normalizedRangeStart?.getTime() === expectedStart?.getTime() &&
            normalizedRangeEnd?.getTime() === expectedEnd?.getTime()
          );
        },
        label: "Last Year",
      },
    ]),
  ];

  const customLocale = {
    ...enUS,
    formatLong: {
      ...enUS.formatLong,
      month: () => 'MMMM',
    },
    localize: {
      ...enUS.localize,
      day: (narrow) => {
        const shortDayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        return shortDayNames[narrow];
      },
      month: (narrow) => {
        const fullMonthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return fullMonthNames[narrow];
      },
    },
  };

  const handleOnChange = (ranges) => {
    const { selection } = ranges;
    
    if (!selection || !selection.startDate || !selection.endDate) {
      isUserSelectingRef.current = false;
      return;
    }
    
    // Mark that user is actively selecting
    isUserSelectingRef.current = true;
    
    const start = new Date(moment.tz(selection.startDate, global.config.getTimeZone()).toDate());
    const startOfDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());

    const end = new Date(moment.tz(selection.endDate, global.config.getTimeZone()).toDate());
    const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

      setDateState({
      startDate: startOfDay,
      endDate: endOfDay,
        key: "selection",
      });
      setState([
        {
        startDate: startOfDay,
        endDate: endOfDay,
          key: "selection",
        },
      ]);
  };

  const handleDateChange = () => {
    // Reset the selecting flag when user confirms selection
    isUserSelectingRef.current = false;
    
    if (dateState && dateState.startDate && dateState.endDate) {
      setDateRangeState(dateState);
      onChange(dateState);
    }
  };

  const handleCancelDateChange = () => {
    // Reset the selecting flag when user cancels
    isUserSelectingRef.current = false;
    
    onCancel();
    setState([
      {
        startDate: (dateRangeState && dateRangeState.startDate) || initialRange.startDate || null,
        endDate: (dateRangeState && dateRangeState.endDate) || initialRange.endDate || null,
        key: "selection",
      },
    ]);
  };

  return (
    <React.Fragment>
      <DateRangePicker
        onChange={handleOnChange}
        showSelectionPreview={true}
        moveRangeOnFirstSelection={false}
        months={months}
        ranges={state}
        staticRanges={staticRanges}
        timeZone={global.config.getTimeZone()}
        locale={customLocale}
        weekStartsOn={appSettings?.weekStartDay && appSettings.weekStartDay === "Monday" ? 1 : 0}
        direction="horizontal"
      />

      <div className="new-calendar-button">
        <button className="calendar-button-new" onClick={handleCancelDateChange}>Cancel</button>
        <button className="calendar-button-new" onClick={handleDateChange} disabled={!isDatesSelected}>OK</button>
      </div>
    </React.Fragment>
  );
};

Calendar.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  initialRange: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }).isRequired,
};

export default Calendar;